import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterService, FilterSettingsModel } from '@syncfusion/ej2-angular-grids';
import { TextBox } from '@syncfusion/ej2-angular-inputs';
import { DialogComponent, TooltipComponent } from '@syncfusion/ej2-angular-popups';
import { EmitType } from '@syncfusion/ej2-base';
import { DataUtil } from '@syncfusion/ej2-data';
import { ToastrService } from 'ngx-toastr';
import { AppGlobals } from '../../shared/globals/appGlobals';
import { fadeInOut } from '../../shared/helpers/animations';
import { DeviceAttribute, IExportDataTask, IExportDataTemplate, IResults, IServiceError, Section } from '../../shared/models/index';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { AuthorizationService, CustomerService, DeviceinventoryService, ExportDataService, SettingsService, StorageManagerService } from '../../shared/services/index';
import { AttributesModalComponent } from '../attributes-modal/attributes-modal.component';
import { BaseGridComponent } from '../base-grid/base-grid.component';
import { FeatureService } from '../../shared/services/feature.service';

@Component({
    selector: 'app-export-data',
    templateUrl: './export-data.component.html',
    styleUrls: ['./export-data.component.scss'],
    animations: [fadeInOut],
    providers: [FilterService],
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false
})
export class ExportDataComponent extends BaseGridComponent implements OnInit, OnDestroy {

  dataScopeEnum: any;
  dataScope: any;
  resolverData: any[] | IServiceError;
  loading = true;
  attributes: DeviceAttribute[];
  templates: IExportDataTemplate[];
  tasks: IExportDataTask[];
  properties: string[] = [];
  editTemplateId: number;
  formatOptions: Object;
  filterSettings: FilterSettingsModel;
  templateToDeleteId: number;
  templateToDeleteName = '';
  errorMessage: string;

  target = '#modalTarget';
  modalPosition: object = { X: 'center', Y: 'center' };
  modalWidth = '30%';
  modalDeleteWidth = '450px';
  modalHeight = 'auto';
  animationSettings: Object = { effect: 'Zoom' };
  isModal = true;
  modalVisible = false;
  tasksCount;
  selectOptions: Object;

  @ViewChild('attrModal', { static: true }) attrModal: AttributesModalComponent;
  @ViewChild('deleteDialog', { static: true }) public deleteDialog: DialogComponent;
  @ViewChild('runDialog', { static: true }) public runDialog: DialogComponent;
  @ViewChild('tooltipObj') tooltipObj: TooltipComponent;

  constructor(public service: DeviceinventoryService, public storage: StorageManagerService, public router: Router,
    public activatedRoute: ActivatedRoute, public toastr: ToastrService, public cdref: ChangeDetectorRef,
    public authorizationService: AuthorizationService, public authenticationService: AuthenticationService,
    public appGlobals: AppGlobals, public exportDataService: ExportDataService, public settingsService: SettingsService,
    public customerService: CustomerService, public featureService: FeatureService) {

    super(service, storage, router, activatedRoute, toastr, cdref, authorizationService, authenticationService,
      appGlobals, exportDataService, settingsService, customerService, featureService);
  }

  ngOnInit(): void {

    this.selectOptions = { persistSelection: true };
    this.formatOptions = { type: 'dateTime', format: 'MM/dd/yyyy hh:mm a' };
    this.sortSettings = { columns: [{ field: 'createDateTime', direction: 'Descending' }] };

    this.permissionsConst = this.appGlobals.permissions;

    this.filterSettings = {
      type: 'Menu',
      operators: {
        stringOperator: [
          { value: 'contains', text: 'contains' },
          { value: 'startsWith', text: 'starts with' },
          { value: 'endsWith', text: 'ends with' },
          { value: 'equal', text: 'equal' }
        ],
        dateOperator: [
          { value: 'equal', text: 'equal' }
        ],
      }
    };

    this.storage.write('activeTab', 'device');
    this.resolverData = this.activatedRoute.snapshot.data['data'];

    if (this.resolverData && (this.resolverData as any).length) {
      this.attributes = this.resolverData[0]?.data?.deviceAttributes;
      var fields = this.attributes?.find(a => a.name === 'DEVICE_KEY_IDENTIFICATION')?.fields;
      var f = fields?.find(a => a.name === 'UDI_DEVICE_IDENTIFIER');
      if (f) {
        f.caption = f.caption + '/EUDAMED ID';
      }
      f = fields?.find(a => a.name === 'UDI_DEVICE_IDENTIFIER_ISSUING_AGENCY');
      if (f) {
        f.caption = f.caption + '/EUDAMED ID Issuing Agency';
      }
      fields = this.attributes?.find(a => a.name === 'BASIC_UDI_DI_REFERENCE')?.fields;
      f = fields?.find(a => a.name === 'BUDI_DEVICE_IDENTIFIER');
      if (f) {
        f.caption = f.caption + '/EUDAMED DI';
      }
      f = fields?.find(a => a.name === 'BUDI_DEVICE_IDENTIFIER_ISSUING_AGENCY');
      if (f) {
        f.caption = f.caption + '/EUDAMED DI Issuing Agency';
      }
      // If the 'sk-device-license-number' feature is not enabled for the user, remove it from the attributes array.
      if (this.resolverData[3]) {
        let isSkDeviceLicenseNumberEnabled = this.resolverData[3] as IResults;
        if (isSkDeviceLicenseNumberEnabled.statusCode === 200 && !isSkDeviceLicenseNumberEnabled.value) {
          this.attributes = this.attributes.filter(x => x.name !== 'DEVICE_LICENSE' && x.caption !== 'DEVICE LICENSE NUMBER');
        }
      }

      this.templates = this.resolverData[1]?.data;

      // DataUtil.parse correctly parse date columns for filtering
      this.tasks = DataUtil.parse.parseJson(this.resolverData[2]?.data as any);

      // remove Choose Action attribute.
      const deviceKeyIdentification: DeviceAttribute = this.attributes.find(a => a.name === 'DEVICE_KEY_IDENTIFICATION');

      if (deviceKeyIdentification) {
        const chooseActionAttrIndex = deviceKeyIdentification.fields.findIndex(f => f.name === 'CHOOSE_ACTION');

        if (chooseActionAttrIndex >= 0) {
          deviceKeyIdentification.fields.splice(chooseActionAttrIndex, 1);
        }
      }

      // get the string list of properties for the autocomplete control's datasource
      this.extractProperties();
    }

    this.loading = false;

    // set focus on the invisible div so the firest tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
    this.initTabIndex();
  }

  initTabIndex() {
    (document.activeElement as HTMLElement).blur();

    const main = document.getElementById('firstDiv');

    if (main) {
      main.focus();
    }
  }


  extractProperties() {

    let sections: Section[] = [];

    if (this.attributes && this.attributes.length) {

      // element here is a section
      this.attributes.forEach(element => {

        // inside there is an array of attributes called "fields"
        if (element.fields && element.fields.length) {

          element.fields.forEach(field => {

            if (!this.properties.includes(field.name)) {
              this.properties.push(field.name);
            }

            sections = this.attrModal.getAttrSections(element);

            // the attribute may have a nested array of sections with fields.
            if (sections && sections.length) {

              sections.forEach(innerSection => {
                if (innerSection.fields && innerSection.fields.length) {

                  innerSection.fields.forEach(innerSectionField => {

                    if (!this.properties.includes(innerSectionField.name)) {
                      this.properties.push(innerSectionField.name);
                    }
                  });
                }
              });
            }
          });
        }
      });

      this.properties = this.properties.sort();
    }
  }

  showAttrDialog(templateId?: number) {
    this.editTemplateId = templateId;
    this.attrModal.dialog.show();
  }

  itemClicked(fileName) {
  }

  // delete dialog
  onDeleteOverlayClick: EmitType<object> = () => {
    this.modalVisible = false;
    this.deleteDialog.hide();
  }

  showDeleteConfirmation(templateId, templateName) {

    this.templateToDeleteId = templateId;

    // need the name for the confirmation dialog
    this.templateToDeleteName = templateName;
    this.modalVisible = true;
    this.deleteDialog.show();
  }

  deleteCancel(event) {
    this.modalVisible = false;
    this.deleteDialog.hide();
  }

  deleteConfirm($event) {

    if (this.templateToDeleteId) {
      this.subscription = this.exportDataService.deleteExportTemplate(this.templateToDeleteId)?.subscribe(response => {

        if (response['isSuccess']) {

          this.modalVisible = false;
          this.deleteDialog.hide();
          this.templateToDeleteId = undefined;
          this.toastr.success(`The template deleted successfully.`);
          this.reloadTemplates();
        } else {
          this.errorMessage = response && response['message'] ? response['message'] : 'Template deletion failed.';
          this.toastr.error(this.errorMessage);
        }

      });
    }

  }

  onDeleteDialogClose(event) {
    this.templateToDeleteId = undefined;
    this.templateToDeleteName = '';
  }

  runTemplate(templateId) {

    if (templateId) {

      this.subscription = this.exportDataService.runExportTemplate(templateId)?.subscribe(response => {

        if (response['isSuccess']) {
          this.reloadGeneratedFiles();
          this.toastr.success(`The template ran successfully.`);
        } else {
          this.errorMessage = response && response['message'] ? response['message'] : 'Template deletion failed.';
          this.toastr.error(this.errorMessage);
        }
      });
    }
  }

  reloadTemplates(success?: boolean) {

    this.subscription = this.exportDataService.getTemplatesList()?.subscribe(response => {

      if (response['isSuccess']) {
        this.templates = response.data;
      }

    });
  }

  reloadGeneratedFiles(success?: boolean) {

    this.subscription = this.exportDataService.getTasks()?.subscribe(response => {

      if (response['isSuccess']) {
        this.tasks = response['data'];
      }
    });
  }

  downloadExportedFile(fileName, filePath) {

    if (fileName && filePath) {

      this.exportDataService.getExportFile(fileName, filePath)?.subscribe(response => {

        if (response['size'] && response['type']) {

          // IE
          if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
            (window.navigator as any).msSaveOrOpenBlob(response, fileName);
          } else {

            // non-IE
            const URL = window.URL || (<any>window).webkitURL;
            const downloadUrl = URL.createObjectURL(response);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileName;
            link.click();
          }
        } else {

          this.toastr.error(`Error downloading Export file`, '', { timeOut: 0, closeButton: true });
        }
      });
    }
  }

  showSFToolTip(event) {

    if (this.isEllipsisActive(event)) {

      if (this.tooltipObj) {
        this.tooltipObj.content = event.target.outerText;
        this.tooltipObj.width = `${event.target.scrollWidth - (event.target.scrollWidth / 100 * 10)}px`;
        this.tooltipObj.position = 'TopCenter';

        this.tooltipObj.open(event.target);
      }
    }
  }

  actionBegin(args: any) {

    const type = args.requestType;

    switch (type) {
      case 'paging':
        this.storeSelectedItems();
        break;

      case 'filterbeforeopen':

        break;

      default:
        break;
    }
  }

  /**
* Triggers when any grid action taken (sort, page, rows/per page number)
* Called from the html actionComplete event
* @param  {any} args
*/
  complete(args: any) {

    switch (args.requestType) {

      case 'refresh':
        this.setDeviceCounter();
        break;
      case 'filtering':
        this.setDeviceCounter();
        break;
      case 'paging':
        this.persistSelectedRows();
        break;
      case 'sorting':
        break;
      case 'filterafteropen':

        if (args.columnType === 'date') {
          args.filterModel.dlgDiv.querySelector(
            '.e-datepicker'
          ).ej2_instances[0].format = 'MM/dd/y';
        }
        break;
    }
  }

  setDeviceCounter() {

    const pageCountMsg = document.getElementsByClassName('e-pagecountmsg')[0];

    if (pageCountMsg) {
      this.pageItemCounter = (pageCountMsg as any).innerText;

      this.tasksCount = this.pageItemCounter.substr(this.pageItemCounter.indexOf('(') + 1, this.pageItemCounter.indexOf(' item') - 1);
    } else {

      this.tasksCount = 0;
    }
  }

  dataBound(args: any) {

    // The grids in this component have the Menu filter enabled.
    // However, this filter contains an inbuilt autocomplete control
    // The code below creates a custom control to add a plain textbox instead of the autocomplete control.

    const inputObj = [];

    for (let x = 0; x < this.grid.columns.length; x++) {

      let filterBox: any;


      if ((this.grid.columns[x] as any).type === 'string' || (this.grid.columns[x] as any).type === null) {          // create textbox for string type column only
        (this.grid.columns[x] as any).filter.ui = {

          create: (args1) => {

            const flValInput: HTMLElement = document.createElement('input');

            args1.target.appendChild(flValInput);

            inputObj[args1.column.field] = new TextBox({
              floatLabelType: 'Never',
            });
            inputObj[args1.column.field].appendTo(flValInput);

            filterBox = args1.target;
          },
          read: (args2) => {

            let isSkip;
            let skipInput;

            if (args2.column.type === 'string') {
              skipInput = ['>', '<', '=', '!'];

              for (let i = 0, value = args2.element.value; i < value.length; i++) {
                const val = value[i];
                if (skipInput.indexOf(val) > -1) {
                  isSkip = true;
                }
              }
            }

            if (!isSkip) {
              args2.fltrObj.filterByColumn(args2.column.field, args2.operator, args2.element.value);
            } else {

              this.toastr['error']('"< > = !" are not valid filter characters', 'Error', { timeOut: 0, closeButton: true });
            }
          },
          write: (args3) => {
            inputObj[args3.column.field].value = args3.filteredValue || '';
          }
        };
      }
    }

    this.setDeviceCounter();
  }
}
